import { POOLED_MARKET_GEO, POOLED_MARKET_LABEL } from '../dtc-commerce-mmm-constants';
import {
  MarketRow,
  MarketSalesRow,
  MarketStanding,
  MarketStandingConst
} from '../dtc-commerce-mmm-types';

export interface MarketSummary {
  geo: string;
  isPooled: boolean;
  spend: number;
  contribution: number;
  sales: number;
  // Average return over the window: what happened, not what the next dollar would do.
  roas: number | null;
  spendShare: number;
  contributionShare: number;
  mediaShareOfSales: number | null;
  // mape_by_geo, in percent. Covers the whole run, not the window.
  accuracy: number | null;
}

export interface MarketTotals {
  spend: number;
  contribution: number;
  sales: number;
  roas: number | null;
  mediaShareOfSales: number | null;
}

export interface ChannelMarketSummary {
  geo: string;
  isPooled: boolean;
  spend: number;
  contribution: number;
  roas: number | null;
  roasLo: number | null;
  roasHi: number | null;
  standing: MarketStanding;
}

export interface ChannelByMarket {
  name: string;
  spend: number;
  contribution: number;
  roas: number | null;
  markets: ChannelMarketSummary[];
  // Named markets only: the pooled market isn't a place to call out.
  aboveCount: number;
  belowCount: number;
}

// `dma_528` -> `528`: the Nielsen code DMA names and map shapes are keyed by.
export const marketDmaCode = (geo: string): string => geo.replace(/^dma_/, '');

// A market's display name, falling back to its code until (or unless) DMA names load.
export const marketDisplayName = (geo: string, dmaNames?: Record<string, string>): string => {
  if (geo === POOLED_MARKET_GEO) return POOLED_MARKET_LABEL;
  const code = marketDmaCode(geo);
  return dmaNames?.[code] ?? `DMA ${code}`;
};

interface Sums {
  spend: number;
  contribution: number;
  ciLo: number;
  ciHi: number;
}

const ratio = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? numerator / denominator : null;

// ROAS for a revenue model, CPA otherwise, matching Channel Performance.
export const efficiencyOf = (
  spend: number,
  contribution: number,
  isRevenue: boolean
): number | null => (isRevenue ? ratio(contribution, spend) : ratio(spend, contribution));

// The market file runs to ~870k rows, so the window compares dates as text instead of parsing
// each one: YYYY-MM-DD text sorts the same as the dates it names.
const filterMarketWindow = <T extends { date: string }>(
  rows: T[],
  startDate: string,
  endDate: string
): T[] => rows.filter(r => r.date >= startDate && r.date <= endDate);

const sumByGeo = (rows: MarketRow[]): Map<string, Sums> => {
  const byGeo = new Map<string, Sums>();
  rows.forEach(r => {
    const sums = byGeo.get(r.geo) ?? { spend: 0, contribution: 0, ciLo: 0, ciHi: 0 };
    sums.spend += r.spend;
    sums.contribution += r.contribution;
    sums.ciLo += r.ciLo;
    sums.ciHi += r.ciHi;
    byGeo.set(r.geo, sums);
  });
  return byGeo;
};

// Named markets by spend, largest first, then the pooled market, so a table's rows add up to
// the total beneath them.
const byPooledLastThenSpend = (
  a: { isPooled: boolean; spend: number },
  b: { isPooled: boolean; spend: number }
): number => Number(a.isPooled) - Number(b.isPooled) || b.spend - a.spend;

/**
 * Every market's spend, media-driven revenue and return over the window, with each one's share
 * of the total. Markets have no range on their return: their channels' ranges can't be added
 * together without overstating it.
 */
export const summarizeMarkets = (
  rows: MarketRow[],
  sales: MarketSalesRow[],
  accuracy: Record<string, number>,
  startDate: string,
  endDate: string
): { markets: MarketSummary[]; total: MarketTotals } => {
  const byGeo = sumByGeo(filterMarketWindow(rows, startDate, endDate));
  const salesByGeo = new Map<string, number>();
  filterMarketWindow(sales, startDate, endDate).forEach(r => {
    salesByGeo.set(r.geo, (salesByGeo.get(r.geo) ?? 0) + r.actualKpi);
  });

  const totalSpend = Array.from(byGeo.values()).reduce((s, g) => s + g.spend, 0);
  const totalContribution = Array.from(byGeo.values()).reduce((s, g) => s + g.contribution, 0);
  const totalSales = Array.from(salesByGeo.values()).reduce((s, v) => s + v, 0);

  const markets = Array.from(byGeo.entries())
    .map(([geo, sums]): MarketSummary => {
      const marketSales = salesByGeo.get(geo) ?? 0;
      return {
        geo,
        isPooled: geo === POOLED_MARKET_GEO,
        spend: sums.spend,
        contribution: sums.contribution,
        sales: marketSales,
        roas: ratio(sums.contribution, sums.spend),
        spendShare: ratio(sums.spend, totalSpend) ?? 0,
        contributionShare: ratio(sums.contribution, totalContribution) ?? 0,
        mediaShareOfSales: ratio(sums.contribution, marketSales),
        accuracy: accuracy[geo] ?? null
      };
    })
    .sort(byPooledLastThenSpend);

  return {
    markets,
    total: {
      spend: totalSpend,
      contribution: totalContribution,
      sales: totalSales,
      roas: ratio(totalContribution, totalSpend),
      mediaShareOfSales: ratio(totalContribution, totalSales)
    }
  };
};

const standingAgainst = (
  national: number | null,
  lo: number | null,
  hi: number | null
): MarketStanding => {
  if (national === null || lo === null || hi === null) return MarketStandingConst.SAME;
  if (lo > national) return MarketStandingConst.ABOVE;
  if (hi < national) return MarketStandingConst.BELOW;
  return MarketStandingConst.SAME;
};

/**
 * One feature's return in each market, against its national return. A market's range is its
 * weekly ranges added up: close enough within one feature, whose effect in a market rests on a
 * single estimate, unlike adding ranges across features. A market stands apart only when its
 * whole range sits on one side of the national return.
 */
export const summarizeChannelByMarket = (
  rows: MarketRow[],
  name: string,
  startDate: string,
  endDate: string
): ChannelByMarket => {
  const byGeo = sumByGeo(
    filterMarketWindow(
      rows.filter(r => r.name === name),
      startDate,
      endDate
    )
  );
  const spend = Array.from(byGeo.values()).reduce((s, g) => s + g.spend, 0);
  const contribution = Array.from(byGeo.values()).reduce((s, g) => s + g.contribution, 0);
  const national = ratio(contribution, spend);

  const markets = Array.from(byGeo.entries())
    .map(([geo, sums]): ChannelMarketSummary => {
      const roasLo = ratio(Math.min(sums.ciLo, sums.ciHi), sums.spend);
      const roasHi = ratio(Math.max(sums.ciLo, sums.ciHi), sums.spend);
      return {
        geo,
        isPooled: geo === POOLED_MARKET_GEO,
        spend: sums.spend,
        contribution: sums.contribution,
        roas: ratio(sums.contribution, sums.spend),
        roasLo,
        roasHi,
        standing: standingAgainst(national, roasLo, roasHi)
      };
    })
    .sort(byPooledLastThenSpend);

  const named = markets.filter(m => !m.isPooled);
  return {
    name,
    spend,
    contribution,
    roas: national,
    markets,
    aboveCount: named.filter(m => m.standing === MarketStandingConst.ABOVE).length,
    belowCount: named.filter(m => m.standing === MarketStandingConst.BELOW).length
  };
};
